/**
 * นโยบาย/ประกาศการทำงาน (Owner/HR) — สร้าง/แก้/ลบ + ดูผู้รับทราบ (หลักฐาน)
 * แก้เนื้อหา/สร้างใหม่ = ฉบับปรับปรุง บังคับใช้ทันที + ส่ง LINE แจ้งพนักงานอัตโนมัติ.
 * เวลา/OT/ลา/วันหยุด = ประกาศระบบ กด "สร้างใหม่จากค่าระบบ" ให้ตรงค่าปัจจุบันได้.
 */
import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, RefreshCw, Users, Megaphone, FileText, Eye } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import {
  useAdminPolicies, useUpsertPolicy, useDeletePolicy, useRegeneratePolicy, usePolicyAcks,
  type AdminPolicy,
} from '../../hooks';

const CATS = [
  { v: 'time', label: 'เวลาทำงาน' }, { v: 'ot', label: 'OT' },
  { v: 'leave', label: 'การลา' }, { v: 'holiday', label: 'วันหยุด' }, { v: 'general', label: 'ทั่วไป' },
] as const;
const catLabel = (v: string) => CATS.find((c) => c.v === v)?.label ?? 'ทั่วไป';

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  const parts = iso.slice(0, 10).split('-');
  const y = Number(parts[0]); const m = Number(parts[1]); const d = Number(parts[2]);
  const mth = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  if (!y || !m) return iso;
  return `${d} ${mth[m]} ${y + 543}`;
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

type Draft = { id?: string; title: string; category: string; body: string; effective_date: string; doc_code: string; published: boolean };
const BLANK: Draft = { title: '', category: 'general', body: '', effective_date: todayISO(), doc_code: '', published: true };

export function OwnerPolicies() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isLoading } = useAdminPolicies();
  const upsert = useUpsertPolicy();
  const del = useDeletePolicy();
  const regen = useRegeneratePolicy();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [acksFor, setAcksFor] = useState<AdminPolicy | null>(null);
  const empCount = data?.employee_count ?? 0;

  function openNew() { setDraft({ ...BLANK }); }
  function openEdit(p: AdminPolicy) {
    setDraft({ id: p.id, title: p.title, category: p.category, body: p.body, effective_date: p.effective_date?.slice(0, 10) || todayISO(), doc_code: p.doc_code || '', published: p.published ?? true });
  }

  async function save() {
    if (!draft) return;
    if (!draft.title.trim()) { toast.error('กรอกหัวข้อประกาศ'); return; }
    try {
      const res = await upsert.mutateAsync({
        id: draft.id, title: draft.title.trim(), category: draft.category, body: draft.body,
        effective_date: draft.effective_date, doc_code: draft.doc_code.trim() || undefined, published: draft.published,
      });
      const n = (res as { notified?: number }).notified ?? 0;
      toast.success(n > 0 ? `บันทึกแล้ว — ส่งแจ้งพนักงาน ${n} คน` : 'บันทึกแล้ว');
      setDraft(null);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }

  async function doRegen(p: AdminPolicy) {
    if (!(await confirm({ title: 'สร้างเนื้อหาใหม่จากค่าระบบ?', message: 'จะดึงค่าปัจจุบัน (เวลา/OT/ลา/วันหยุด) มาเขียนใหม่เป็นฉบับปรับปรุง และส่งแจ้งพนักงานให้รับทราบใหม่' }))) return;
    try { const r = await regen.mutateAsync(p.id); toast.success(`อัปเดตแล้ว — แจ้งพนักงาน ${(r as { notified?: number }).notified ?? 0} คน`); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'); }
  }

  async function doDelete(p: AdminPolicy) {
    if (!(await confirm({ title: `ลบ “${p.title}”?`, message: 'ลบถาวร — ประวัติการรับทราบจะถูกลบด้วย', danger: true }))) return;
    try { await del.mutateAsync(p.id); toast.success('ลบแล้ว'); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ'); }
  }

  return (
    <AdminLayout title="นโยบาย / ประกาศการทำงาน" actions={<button onClick={openNew} className="btn-cta btn-slim inline-flex items-center gap-1"><Plus className="w-4 h-4" /> ประกาศใหม่</button>}>
      <div className="max-w-2xl mx-auto space-y-3 pb-10">
        <p className="text-sm text-warm-gray">
          ข้อตกลงร่วมบริษัท↔พนักงาน · บันทึก รหัสเอกสาร/วันประกาศ/วันมีผล/ฉบับที่ · แก้แล้วส่ง LINE แจ้งพนักงานอัตโนมัติให้กดรับทราบ
        </p>

        {isLoading && <div className="card p-6 text-center text-muted">กำลังโหลด…</div>}

        {(data?.items ?? []).map((p) => (
          <div key={p.id} className="card p-4">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-xl bg-gold-wash text-gold-deep grid place-items-center shrink-0"><FileText className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-display font-semibold uppercase tracking-wider text-gold-deep">{catLabel(p.category)}</span>
                  {p.source === 'system' && <span className="text-[10px] bg-cream text-ink rounded-full px-2 py-0.5">ระบบ</span>}
                  {!p.published && <span className="text-[10px] bg-gray-200 text-warm-gray rounded-full px-2 py-0.5">ปิดเผยแพร่</span>}
                </div>
                <p className="font-display font-semibold text-ink mt-0.5">{p.title}</p>
                <p className="text-xs text-warm-gray mt-0.5">
                  {p.doc_code ? `${p.doc_code} · ` : ''}ฉบับที่ {p.version} · มีผล {fmtDate(p.effective_date)}
                </p>
                <button onClick={() => setAcksFor(p)} className="text-xs text-gold-deep font-medium inline-flex items-center gap-1 mt-1.5">
                  <Users className="w-3.5 h-3.5" /> รับทราบแล้ว {p.ack_count}/{empCount} คน
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3 justify-end">
              {p.source === 'system' && (
                <button onClick={() => doRegen(p)} disabled={regen.isPending} className="btn-secondary btn-slim inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> สร้างใหม่จากค่าระบบ</button>
              )}
              <button onClick={() => openEdit(p)} className="btn-secondary btn-slim inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> แก้ไข</button>
              {p.source === 'custom' && (
                <button onClick={() => doDelete(p)} className="btn-slim inline-flex items-center gap-1 text-[#b4564f] border border-[#b4564f]/40 rounded-full px-2.5 py-1"><Trash2 className="w-3.5 h-3.5" /> ลบ</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* แก้ไข / สร้าง */}
      <Modal open={!!draft} onClose={() => setDraft(null)} title={draft?.id ? 'แก้ไขประกาศ' : 'ประกาศใหม่'} size="max-w-lg">
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="label">หัวข้อประกาศ</label>
              <input className="input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="เช่น ระเบียบการแต่งกาย" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">หมวด</label>
                <select className="input" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                  {CATS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">วันมีผลบังคับใช้</label>
                <input type="date" className="input" value={draft.effective_date} onChange={(e) => setDraft({ ...draft, effective_date: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="label">รหัสเอกสาร (เว้นว่าง = ออกให้อัตโนมัติ)</label>
              <input className="input num" value={draft.doc_code} onChange={(e) => setDraft({ ...draft, doc_code: e.target.value })} placeholder="MMS-GEN-2569-01" />
            </div>
            <div>
              <label className="label">เนื้อหา</label>
              <textarea className="input min-h-[180px]" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="พิมพ์รายละเอียดประกาศ… (ขึ้นบรรทัดใหม่ได้)" />
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} />
              เผยแพร่ให้พนักงานเห็น
            </label>
            <div className="rounded-lg bg-gold-wash text-gold-deep text-xs p-3 flex items-start gap-2">
              <Megaphone className="w-4 h-4 shrink-0 mt-0.5" />
              บันทึกแล้ว ถ้าแก้เนื้อหา/หัวข้อ จะถือเป็นฉบับปรับปรุง (บังคับใช้ทันที) และส่ง LINE แจ้งพนักงานทุกคนให้กดรับทราบใหม่
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setDraft(null)} className="btn-secondary">ยกเลิก</button>
              <button onClick={save} disabled={upsert.isPending} className="btn-cta">{upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก & แจ้งพนักงาน'}</button>
            </div>
          </div>
        )}
      </Modal>

      {/* ผู้รับทราบ (หลักฐาน) */}
      <AcksModal policy={acksFor} onClose={() => setAcksFor(null)} empCount={empCount} />
    </AdminLayout>
  );
}

function AcksModal({ policy, onClose, empCount }: { policy: AdminPolicy | null; onClose: () => void; empCount: number }) {
  const { data, isLoading } = usePolicyAcks(policy?.id ?? null);
  const acks = (data?.acks ?? []).filter((a) => a.version === (policy?.version ?? 0));
  return (
    <Modal open={!!policy} onClose={onClose} title="ผู้รับทราบ (หลักฐาน)" size="max-w-md">
      {policy && (
        <div className="space-y-3">
          <p className="text-sm text-warm-gray">{policy.title} · ฉบับที่ {policy.version}</p>
          <div className="flex items-center gap-2 text-sm">
            <Eye className="w-4 h-4 text-gold-deep" /> รับทราบฉบับปัจจุบันแล้ว <b className="num">{acks.length}/{empCount}</b> คน
          </div>
          {isLoading && <p className="text-sm text-muted">กำลังโหลด…</p>}
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {acks.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-[color:rgba(13,11,10,.06)] py-1.5">
                <span className="text-ink">{a.display_name || a.emp_code || '-'}</span>
                <span className="num text-xs text-muted">{new Date(a.acknowledged_at).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}</span>
              </div>
            ))}
            {!isLoading && acks.length === 0 && <p className="text-sm text-muted text-center py-4">ยังไม่มีใครรับทราบฉบับนี้</p>}
          </div>
        </div>
      )}
    </Modal>
  );
}
